import { filing } from './toolkit';

/**
 * Family tree affidavit (Form FT-1, 2 pages).
 * Page 1: decedent, spouse, and the descendant columns (children, grandchildren, great grandchildren).
 * Page 2: grandparents, parents, aunts and uncles, first cousins and first cousins once removed.
 * Every name comes from the estate's family tree (Participants and their relationships); the
 * placement and order down each column is derived by the family tree service (ordinal "1.2" is the
 * second child of the first child).
 */
const P1 = (n: number) => `topmostSubform[0].Page1[0].TextField${n}[0]`;
const P2 = (n: number) => `topmostSubform[0].Page2[0].TextField${n}[0]`;

export default filing('file-1786322686542-5269068d-f8f8-4b37-a383-320d0c75f78f.pdf', 'Family tree affidavit', f => {
  // Page 1, first column: decedent and spouse.
  f.shared(P1(6), 'decedent.fullName', 'Decedent (name)');
  f.template(P1(11), '{{decedentSpouse.fullName}}', 'Name of spouse');

  // Computed from the decedent's marital status and the spouse's participant record.
  f.shared(P1(17), 'decedent.spouseDeceasedMark', 'Spouse deceased: mark (widowed, or the spouse died before the decedent)');
  f.shared(P1(20), 'decedent.divorcedMark', 'Divorced: mark (decedent marital status is Divorced)');
  f.shared(P1(30), 'decedent.neverMarriedMark', 'Never married: mark (decedent marital status is Single and no spouse is recorded)');
  f.template(P1(18), '{{decedentSpouse.dateOfDeath}}', "Spouse deceased: date (the spouse's date of death in Participants)");
  f.input(P1(21), 'divorceDate', 'Divorced: date of divorce', 'Decedent and spouse', 'date',
    'Date of the judgment of divorce. Leave blank unless divorced.');

  // Page 1: children (one line per child, oldest first) with their children beside them.
  const children = [5, 12, 22, 32, 38];
  children.forEach((field, i) =>
    f.template(P1(field), `{{children[ordinal=${i + 1}].fullName}}`, `Children: child ${i + 1}`));

  const grandchildren: [number, string][] = [
    [4, '1.1'], [7, '1.2'], [8, '1.3'], [9, '1.4'],
    [13, '2.1'], [15, '2.2'], [16, '2.3'],
    [23, '3.1'], [25, '3.2'], [26, '3.3'],
    [31, '4.1'], [33, '4.2'], [34, '4.3'],
    [37, '5.1'], [39, '5.2'], [40, '5.3'],
  ];
  grandchildren.forEach(([field, ordinal]) => {
    const [parent, n] = ordinal.split('.');
    f.template(P1(field), `{{grandchildren[ordinal=${ordinal}].fullName}}`, `Grandchildren: child ${parent}, grandchild ${n}`);
  });

  // Great grandchildren are a free list: each line names whose child they are.
  [1, 2, 3, 10, 14, 19, 24, 27, 28, 29, 35, 36, 41, 42, 43].forEach((field, i) =>
    f.template(P1(field), `{{greatGrandchildren[${i}].fullName}} (of {{greatGrandchildren[${i}].parent.fullName}})`,
      `Great grandchildren: line ${i + 1} (name and parent)`));

  // Page 1 affidavit.
  f.notary(P1(44), 'Page 1 affidavit venue: County of (where sworn)');
  f.shared(P1(45), 'executor.fullName', 'Page 1 affidavit: deponent name (being duly sworn, states that the charts are correct)');
  f.notary(P1(46), 'Page 1 jurat: sworn to me on (date)');
  f.notary(P1(47), 'Page 1 jurat: Notary Public signature');

  // Page 2: grandparents and parents.
  f.template(P2(65), '{{paternalGrandfather.fullName}}', 'Paternal grandfather');
  f.template(P2(73), '{{paternalGrandmother.fullName}}', 'Paternal grandmother');
  f.template(P2(74), '{{father.fullName}}', 'Father of decedent');
  f.template(P2(91), '{{maternalGrandfather.fullName}}', 'Maternal grandfather');
  f.template(P2(100), '{{maternalGrandmother.fullName}}', 'Maternal grandmother');
  f.template(P2(101), '{{mother.fullName}}', 'Mother of decedent');

  // Page 2: aunts and uncles, first cousins and first cousins once removed, per side.
  const side = (name: 'paternal' | 'maternal', label: string, aunts: number[], cousins: number[], removed: number[]) => {
    aunts.forEach((field, i) =>
      f.template(P2(field), `{{${name}AuntsUncles[ordinal=${i + 1}].fullName}}`, `${label} aunts and uncles: ${i + 1}`));
    cousins.forEach((field, i) => {
      const ordinal = `${Math.floor(i / 3) + 1}.${(i % 3) + 1}`;
      f.template(P2(field), `{{${name}FirstCousins[ordinal=${ordinal}].fullName}}`,
        `${label} first cousins: aunt or uncle ${Math.floor(i / 3) + 1}, cousin ${(i % 3) + 1}`);
    });
    removed.forEach((field, i) =>
      f.template(P2(field), `{{${name}FirstCousinsOnceRemoved[${i}].fullName}} (of {{${name}FirstCousinsOnceRemoved[${i}].parent.fullName}})`,
        `${label} first cousins once removed: line ${i + 1} (name and deceased first cousin parent)`));
  };
  side('paternal', 'Paternal', [50, 58, 67], [48, 51, 53, 56, 59, 61, 64, 68, 70], [49, 52, 54, 55, 57, 60, 62, 63, 66, 69, 71, 72]);
  side('maternal', 'Maternal', [77, 85, 94], [75, 78, 80, 83, 86, 88, 92, 95, 97], [76, 79, 81, 82, 84, 87, 89, 90, 93, 96, 98, 99]);

  // Page 2 affidavit.
  f.notary(P2(102), 'Page 2 affidavit venue: County of (where sworn)');
  f.shared(P2(103), 'executor.fullName', 'Page 2 affidavit: deponent name (being duly sworn, states that the charts are correct)');
  f.notary(P2(104), 'Page 2 jurat: sworn to before me on (date)');
  f.notary(P2(105), 'Page 2 jurat: Notary Public signature');
});
